/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { FullProductInput, getFullProducts, getProductDetailsCriteriaFromActionInput } from '@msdyn365-commerce-modules/retail-actions';
import { getSavedDesignsAsync } from '../../../dataActions/DataActionExtension.g';

/**
 * Input class for getActiveWishlistItems data action.
 */
export class ActiveWishlistInput implements IActionInput {
    public getCacheKey = () => 'ActiveWishlistItems';

    public getCacheObjectType = () => 'ActiveWishlistItems';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (inputData: ICreateActionContext) => {
    return new ActiveWishlistInput();
};

/**
 * Calls the Retail API and returns a CommerceList object based on the passed GetCartInput.
 * @param input
 * @param ctx
 */
export async function getActiveWishlistItems(input: ActiveWishlistInput, ctx: IActionContext): Promise<FullProduct[]> {
    // If no cart ID is provided in input, we need to create a cart object
    if (!input) {
        throw new Error('[getActiveWishlistItems]No valid Input was provided, failing');
    }

    if (ctx.requestContext.user.isAuthenticated) {
        const savedDesigns: any = await getSavedDesignsAsync({ callerContext: ctx, queryResultSettings: {} });

        if (savedDesigns && savedDesigns.Result && savedDesigns.Result.length > 0) {
            const productInputs: FullProductInput[] = [];
            savedDesigns.Result.forEach((currSavedDesign: any) => {
                if (currSavedDesign.ProductID) {
                    productInputs.push(
                        new FullProductInput(
                            currSavedDesign.ProductID,
                            ctx.requestContext.apiSettings,
                            getProductDetailsCriteriaFromActionInput(ctx)
                        )
                    );
                }
            });

            if (productInputs.length > 0) {
                return getFullProducts(productInputs, ctx)
                    .then(products => {
                        if (products) {
                            const mappedProducts: any = [];
                            savedDesigns.Result.forEach((currSavedDesign: any) => {
                                const product: any = products.find((p: any) => {
                                    return currSavedDesign.ProductID === p.ProductDetails.RecordId;
                                });
                                if (product && currSavedDesign.ProductID) {
                                    const productInfo: any = { ...product };
                                    // find snapshotid...
                                    productInfo.snapShotId = currSavedDesign.SnapShotID;
                                    productInfo.b2cPrice = currSavedDesign.B2CPrice;
                                    productInfo.b2bPrice = currSavedDesign.B2BPrice;
                                    productInfo.refSavedDesign = currSavedDesign;
                                    mappedProducts.push(productInfo);
                                }
                            });

                            return mappedProducts;
                        }
                        return <FullProduct[]>[];
                    })
                    .catch(error => {
                        ctx.trace(error);
                        ctx.telemetry.error(error.message);
                        ctx.telemetry.debug('[getActiveWishlistItems]Unable to hydrate cart with product information');
                        throw new Error('[getActiveWishlistItems]Unable to hydrate cart with product information');
                    });
            }
            ctx.trace('No productIds found for wishlist');
        } else {
            ctx.trace('[getActiveWishlistItems]Not able to get wishlists for the customer');
            return <FullProduct[]>[];
        }
    }
    ctx.trace('[getActiveWishlistItems]User token not found for wishlists');
    return <FullProduct[]>[];
}

export const getActiveWishlistItemsDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/wishlist/wishlist-items/get-items-in-wishlist',
    action: <IAction<FullProduct[]>>getActiveWishlistItems,
    input: createInput
});

export default getActiveWishlistItemsDataAction;
